import { Link, useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeading } from "@/components/ui/section-heading";
import {
  useSEO,
  useSEOSettings,
  generateBreadcrumbSchema,
  generateKnowledgeGraphSchema,
  type SiteInfoSettings,
} from "@/lib/seo-utils";
import { MapPin, Clock, ExternalLink, ArrowLeft } from "lucide-react";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import type { NearbyAttraction } from "@shared/schema";
import { optimizedUrl, optimizedSrcSet } from "@/lib/img";
import { useBookCta } from "@/hooks/use-book-href";
import { formatDistanceFromHomes, attractionPageTitle, sentenceCase } from "@shared/attraction-copy";

type AttractionPage = NearbyAttraction & {
  related_posts?: Array<{ id: string; title: string; slug: string; excerpt: string | null; reading_time_minutes: number | null }>;
};

export default function NearbyAttractionPage() {
  const { slug } = useParams<{ slug: string }>();
  const { seoSettings } = useSEOSettings();
  const bookCta = useBookCta();
  const { data: siteSettings } = useQuery<SiteInfoSettings>({
    queryKey: ["/api/settings/site_info"],
    retry: false,
  });

  const { data: attraction, isLoading, error } = useQuery<AttractionPage>({
    queryKey: ["/api/nearby-attractions", slug],
    queryFn: async () => {
      const res = await fetch(`/api/nearby-attractions/${slug}`);
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
    enabled: !!slug,
  });

  const seoHead = useSEO({
    title: attraction ? attractionPageTitle(attraction) : "Nearby places",
    description: attraction
      ? (() => {
          // Facts in order of use, added while the whole line still fits a search snippet.
          const parts = [
            (attraction.description || attraction.name).replace(/\.$/, ""),
            attraction.distance ? sentenceCase(`${formatDistanceFromHomes(attraction.distance)} from our oceanfront homes in Ka'a'awa`) : "Near our oceanfront homes in Ka'a'awa",
            attraction.hours ? `Open ${attraction.hours}` : "",
            attraction.ticket_price ? attraction.ticket_price : "",
          ].filter(Boolean);
          let out = "";
          for (const part of parts) {
            const next = out ? `${out} ${part}.` : `${part}.`;
            if (next.length > 158 && out) break;
            out = next;
          }
          return out;
        })()
      : undefined,
    canonical: slug ? `https://vacationrentaloahu.co/nearby/${slug}` : undefined,
    ogImage: "https://vacationrentaloahu.co/images/og-sharing-image.jpg",
    structuredData: attraction
      ? [
          generateKnowledgeGraphSchema({ seoSettings, siteInfoSettings: siteSettings }),
          generateBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Ka'a'awa Guide", url: "/kaaawa" },
            { name: attraction.name, url: `/nearby/${slug}` },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "TouristAttraction",
            name: attraction.name,
            description: attraction.detailed_description || attraction.description,
            url: `https://vacationrentaloahu.co/nearby/${slug}`,
            ...(attraction.image_url && { image: attraction.image_url }),
            ...(attraction.address && {
              address: {
                "@type": "PostalAddress",
                streetAddress: attraction.address,
                addressRegion: "HI",
                addressCountry: "US",
              },
            }),
            ...(attraction.lat &&
              attraction.lng && {
                geo: {
                  "@type": "GeoCoordinates",
                  latitude: parseFloat(String(attraction.lat)),
                  longitude: parseFloat(String(attraction.lng)),
                },
              }),
          },
        ]
      : [],
  });

  if (isLoading) {
    return (
      <>
        {seoHead}
        <div className="min-h-screen bg-shell pb-24 lg:pb-0 selection:bg-sun selection:text-shell">
          <Header />
          <main className="pt-32 pb-16 container mx-auto px-6 max-w-3xl">
            <Skeleton className="h-10 w-2/3 mb-4 bg-sand" />
            <Skeleton className="h-64 w-full mb-6 bg-sand rounded-2xl" />
            <Skeleton className="h-32 w-full bg-sand" />
          </main>
          <Footer />
        </div>
      </>
    );
  }

  if (error || !attraction) {
    return (
      <>
        {seoHead}
        <div className="min-h-screen bg-shell pb-24 lg:pb-0 selection:bg-sun selection:text-shell">
          <Header />
          <main className="pt-40 pb-16 container mx-auto px-6 text-center">
            <h1 className="text-luxury-3xl font-serif text-forest mb-6">Attraction not found</h1>
            <Link href="/kaaawa" className="inline-flex items-center text-ink border-b border-ink pb-1 hover:text-ink-soft hover:border-ink-soft transition-colors">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Ka'a'awa Guide
            </Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      {seoHead}
      <div className="min-h-screen bg-shell pb-24 lg:pb-0 selection:bg-sun selection:text-shell">
        <Header />
        <main id="main-content" className="pt-32 pb-20 md:pb-28">
          <div className="container mx-auto px-6 max-w-3xl">
            <Breadcrumbs
              items={[
                { name: "Home", url: "/" },
                { name: "Ka'a'awa", url: "/kaaawa" },
                { name: attraction.name, url: `/nearby/${slug}` },
              ]}
              className="mb-8"
            />
            
            <h1 className="text-luxury-5xl font-serif font-normal text-forest mb-4 tracking-luxury-tight">
              {attraction.name}
            </h1>
            
            {attraction.distance && (
              <p className="flex items-center gap-2 text-ink-soft mb-10 text-lg">
                <MapPin className="h-5 w-5 text-driftwood" aria-hidden="true" />
                {formatDistanceFromHomes(attraction.distance)} from our homes
              </p>
            )}

            {attraction.image_url && (
              <img
                src={optimizedUrl(attraction.image_url, 960)}
                srcSet={optimizedSrcSet(attraction.image_url, [480, 768, 960, 1200])}
                sizes="(max-width: 768px) 100vw, 768px"
                alt={`${attraction.name} - Oahu attraction near Ka'a'awa vacation rental`}
                className="w-full rounded-2xl object-cover max-h-[500px] mb-12 shadow-sm border border-sand-deep"
                width={800}
                height={500}
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            )}

            <div className="text-luxury-lg text-ink-soft leading-relaxed space-y-6 mb-12">
              <p className="text-xl text-ink">{attraction.description}</p>
              {attraction.detailed_description && (
                <p>{attraction.detailed_description}</p>
              )}
            </div>

            {/* Everything on file, as a fact list a reader or an answer engine can lift. */}
            <dl className="mb-12 grid gap-x-8 gap-y-4 border-t border-sand-deep pt-8 font-sans text-[15px] sm:grid-cols-2" data-testid="attraction-facts">
              {attraction.distance && (
                <div><dt className="text-[11px] uppercase tracking-[0.2em] text-driftwood">From the homes</dt><dd className="mt-1 text-ink">{formatDistanceFromHomes(attraction.distance)}</dd></div>
              )}
              {attraction.hours && (
                <div><dt className="text-[11px] uppercase tracking-[0.2em] text-driftwood">Hours</dt><dd className="mt-1 flex items-center gap-2 text-ink"><Clock className="h-4 w-4 text-driftwood" aria-hidden="true" />{attraction.hours}</dd></div>
              )}
              {attraction.ticket_price && (
                <div><dt className="text-[11px] uppercase tracking-[0.2em] text-driftwood">Admission</dt><dd className="mt-1 text-ink">{attraction.ticket_price}</dd></div>
              )}
              {attraction.address && (
                <div><dt className="text-[11px] uppercase tracking-[0.2em] text-driftwood">Address</dt><dd className="mt-1 text-ink">{attraction.address}</dd></div>
              )}
              {attraction.phone_number && (
                <div><dt className="text-[11px] uppercase tracking-[0.2em] text-driftwood">Phone</dt><dd className="mt-1 text-ink"><a href={`tel:${attraction.phone_number.replace(/[^+\d]/g, "")}`} className="underline underline-offset-4">{attraction.phone_number}</a></dd></div>
              )}
              {attraction.website_url && (
                <div><dt className="text-[11px] uppercase tracking-[0.2em] text-driftwood">Website</dt><dd className="mt-1"><a href={attraction.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-ink underline underline-offset-4 hover:text-sun-hover">Official website <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /></a></dd></div>
              )}
              {attraction.category && (
                <div><dt className="text-[11px] uppercase tracking-[0.2em] text-driftwood">Type</dt><dd className="mt-1 capitalize text-ink">{attraction.category}</dd></div>
              )}
            </dl>

            {Array.isArray(attraction.tags) && attraction.tags.length > 0 && (
              <ul className="mb-12 flex flex-wrap gap-2" aria-label="Good for" data-testid="attraction-tags">
                {attraction.tags.map((t) => (
                  <li key={t} className="rounded-full border border-sand-deep bg-sand/60 px-3 py-1 font-sans text-[12px] text-ink">{t}</li>
                ))}
              </ul>
            )}

            {Array.isArray(attraction.gallery_images) && (attraction.gallery_images as string[]).filter((g) => typeof g === "string").length > 0 && (
              <ul className="mb-12 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="attraction-gallery">
                {(attraction.gallery_images as string[]).filter((g) => typeof g === "string").slice(0, 6).map((src, i) => (
                  <li key={src}>
                    <img src={optimizedUrl(src, 480)} alt={`${attraction.name}, photo ${i + 1}`} className="aspect-[4/3] w-full rounded-xl border border-sand-deep object-cover" loading="lazy" decoding="async" width={480} height={360} />
                  </li>
                ))}
              </ul>
            )}

            {attraction.distance && (
              <section className="mb-12" data-testid="attraction-getting-there">
                <h2 className="mb-3 font-serif text-luxury-2xl text-forest">Getting there from the homes</h2>
                <p className="font-sans text-luxury-base leading-relaxed text-ink-soft">
                  {attraction.name} is {formatDistanceFromHomes(attraction.distance)} from our homes in Ka'a'awa. Most of the drive is on Kamehameha Highway along the windward coast, so leave a little extra time on weekends and in the late afternoon, when traffic through Kaneohe builds.
                  {attraction.hours ? ` Check the hours above before you set out; they change seasonally.` : ""}
                </p>
              </section>
            )}

            {attraction.related_posts && attraction.related_posts.length > 0 && (
              <section className="mb-16" data-testid="attraction-guides">
                <h2 className="mb-4 font-serif text-luxury-2xl text-forest">Guides that cover {attraction.name.split(" (")[0]}</h2>
                <ul className="divide-y divide-sand-deep border border-sand-deep bg-white">
                  {attraction.related_posts.map((p) => (
                    <li key={p.id}>
                      <Link href={`/blog/${p.slug}`} className="group block px-5 py-4 hover:bg-sand/40" data-testid={`attraction-guide-${p.slug}`}>
                        <span className="block font-serif text-[18px] leading-snug text-forest group-hover:text-ink">{p.title}</span>
                        {p.excerpt && <span className="mt-1 block font-sans text-[14px] text-ink-soft line-clamp-2">{p.excerpt}</span>}
                        {p.reading_time_minutes ? <span className="mt-1 block font-sans text-[10px] uppercase tracking-[0.24em] text-driftwood">{p.reading_time_minutes} min read</span> : null}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="p-10 rounded-2xl bg-sand border border-sand-deep text-center">
              <SectionHeading 
                title="Stay nearby"
                lede={`Either of our Ka'a'awa oceanfront homes makes an easy base for visiting ${attraction.name} and exploring the windward coast.`}
                className="mb-8"
              />
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link href={bookCta.href} data-primary-book-cta className="inline-flex items-center justify-center bg-sun text-shell hover:bg-sun-hover font-sans font-semibold uppercase tracking-[0.08em] px-8 py-4 rounded-lg transition-transform duration-300 hover:-translate-y-0.5">
                  {bookCta.choosing ? "Choose a Home" : "Book Your Stay"}
                </Link>
                <Link href="/properties" className="inline-flex items-center justify-center bg-transparent border border-ink text-ink hover:bg-ink hover:text-shell font-sans font-semibold uppercase tracking-[0.08em] px-8 py-4 rounded-lg transition-all duration-300">
                  See Our Homes
                </Link>
              </div>
            </div>
          </div>
        </main>
        <StickyMobileBookCta />
        <Footer />
      </div>
    </>
  );
}
